/**
 * Centralized copy for the Lean Ads marketing homepage.
 * Tone: clear, boring-professional, compliance-friendly.
 */

export const heroCopy = {
  subtitle:
    "Lean Ads helps businesses and marketing agencies plan, execute, and optimise paid advertising campaigns through data-driven strategy and proprietary reporting and campaign workflow tools.",
  supportingLine:
    "We combine advertising expertise, campaign management, and performance analytics to deliver transparent and lean campaign operations.",
  ctaPrimary: "Contact us",
  ctaSecondary: "Our services",
  supportingLineSmall: "Supporting European businesses and marketing agencies.",
} as const;

export const fallingNotificationsCopy = [
  { title: "Back-to-School Campaign", subtitle: "Campaign is live" },
  {
    title: "Autumn Sales Campaign",
    subtitle: "Mid-campaign: performance at 117% of goal",
  },
  { title: "Recruitment Campaign", subtitle: "End campaign report is ready" },
  { title: "September Campaign", subtitle: "Campaign setup complete" },
  { title: "Black Friday Campaign", subtitle: "Media plan is ready" },
  { title: "Shop opening Campaign", subtitle: "Assets are valid" },
  {
    title: "Summer Sales Campaign",
    subtitle: "Campaign finished: 132% of goal achieved",
  },
] as const;

export const servicesCopy = {
  subtitle:
    "Operational management for digital advertising campaigns.",
  services: [
    {
      title: "Campaign Strategy",
      icon: "Strategy",
      text: "Planning and structuring digital advertising campaigns including platform selection, audience targeting, and budget allocation.",
    },
    {
      title: "Campaign Execution",
      icon: "Settings",
      text: "Setup, optimisation, and operational management of paid advertising campaigns across major digital advertising platforms.",
    },
    {
      title: "Advanced Reporting",
      icon: "Assessment",
      text: "Clients access a proprietary dashboard providing campaign monitoring, reporting, and performance analytics.",
    },
  ],
} as const;

export const whoWeWorkWithCopy = `Lean Ads collaborates with European businesses and marketing agencies that require specialised expertise in digital advertising operations.
Agencies may partner with Lean Ads to outsource campaign management and optimisation while maintaining strategic relationships with their own clients.`;

export const trustedByCopy = {
  quote:
    " Lean Ads helps us manage and optimise digital advertising campaigns efficiently while simplifying the campaign workflow and maintaining full visibility on performance. ",
  author: "Nicolas Sebti",
  role: "Founder",
  company: "#NYAC",
  companyDescription: "Digital Marketing Agency - Paris, France",
  companyUrl: "https://www.linkedin.com/company/-nyac",
} as const;

export const platformsCopy = {
  tagline: "Delivering campaigns across leading advertising platforms",
  platformNames: [
    "Instagram",
    "Facebook",
    "TikTok",
    "YouTube",
    "Pinterest",
    "Snapchat",
    "Twitch",
    "LinkedIn",
  ],
} as const;

export const howWeWorkCopy = [
  {
    title: "Campaign Strategy",
    text: "Define campaign objectives, audience targeting, and media budget allocation.",
  },
  {
    title: "Implementation",
    text: "Configure and launch campaigns across relevant advertising platforms.",
  },
  {
    title: "Optimisation",
    text: "Continuously monitor performance and optimise campaigns throughout delivery.",
  },
  {
    title: "Reporting",
    text: "Provide transparent reporting through dashboards and periodic performance reviews.",
  },
] as const;

export const whatYouCanExpectCopy = {
  strategy: "Clear strategy and execution plan",
  implementation: "Disciplined setup and fast launches across platforms",
  optimisation: "Continuous optimisation during delivery",
  reporting: "Transparent reporting and performance reviews",
} as const;

export const aboutCopy = {
  subtitle: '35+ years of combined experience helping brands grow through digital advertising.',
  firstParagraph: `Lean Ads was founded by two experienced advertising professionals with a shared focus on performance and operational excellence.`,
  secondParagraph: `Our mission is to help businesses and marketing agencies maximise the value of their advertising investments through disciplined campaign execution and transparent reporting.`,
};

export const foundersCopy = [
  {
    name: "Amaury de la Taille",
    title: "Director",
    photo: "/static/founders/Amaury.webp",
    linkedInUrl: "https://www.linkedin.com/in/admaury/?locale=en-US",
  },
  {
    name: "Joyner Thomias",
    title: "Campaign Manager",
    photo: "/static/founders/Joyner.webp",
    linkedInUrl: "https://linkedin.com/in/joyner-thomias/?locale=en-US",
  },
] as const;

export const contactCopy = {
  email: "contact@leanads.io",
  linkedInUrl: "https://www.linkedin.com/company/lean-ads",
  /** Only display if it matches official info */
  showRegisteredOffice: true,
  registeredOffice: "32 Kinburn Street, London SE16 6DW, United Kingdom",
} as const;

export const footerCopy = {
  company: {
    title: "Company",
    links: [
      { label: "About Us", href: "/#about" },
      { label: "Our Services", href: "/#services" },
      { label: "Contact", href: "/#contact" },
    ],
  },
  legal: {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/privacy-policy" },
      { label: "Terms of Service", href: "/terms-of-service" },
      { label: "Cookie Policy", href: "/cookie-policy" },
    ],
  },
  connect: {
    title: "Connect",
    linkedInUrl: "https://www.linkedin.com/company/lean-ads",
  },
  copyright: "© 2026 Lean Ads LLP. All rights reserved.",
  registration: "Registered in England & Wales — OC455326",
} as const;
